import { useParams, Link } from "react-router-dom";
import { categories, articles } from "@/mocks/articles";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";

const articleImages: Record<string, string> = {
  "article-intro-01":
    "https://readdy.ai/api/search-image?query=Clean%20modern%20minimalist%20workspace%20with%20laptop%20on%20wooden%20desk%2C%20natural%20light%2C%20professional%20atmosphere%2C%20editorial%20photography%2C%20warm%20cream%20tones%2C%20calm%20and%20focused%20mood%2C%20soft%20shadows&width=800&height=450&seq=article-intro-01&orientation=landscape",
  "article-install-02":
    "https://readdy.ai/api/search-image?query=Smartphone%20with%20app%20installation%20screen%2C%20clean%20minimal%20setup%2C%20warm%20lighting%2C%20modern%20flat%20lay%2C%20tech%20lifestyle%2C%20professional%20photography%2C%20soft%20cream%20background&width=800&height=450&seq=article-install-02&orientation=landscape",
  "article-fake-03":
    "https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20digital%20screen%2C%20cybersecurity%20concept%2C%20minimalist%20design%2C%20warm%20professional%20atmosphere%2C%20abstract%20tech%20patterns%2C%20clean%20editorial%20style&width=800&height=450&seq=article-fake-03&orientation=landscape",
  "article-security-04":
    "https://readdy.ai/api/search-image?query=Digital%20security%20shield%20concept%2C%20padlock%20on%20abstract%20background%2C%20warm%20golden%20tones%2C%20minimalist%20tech%20aesthetic%2C%20professional%20data%20protection%20theme%2C%20clean%20modern%20design&width=800&height=450&seq=article-security-04&orientation=landscape",
  "article-faq-05":
    "https://readdy.ai/api/search-image?query=FAQ%20help%20center%20concept%2C%20organized%20documents%20and%20icons%2C%20warm%20professional%20atmosphere%2C%20clean%20minimalist%20workspace%2C%20editorial%20style%2C%20soft%20natural%20light&width=800&height=450&seq=article-faq-05&orientation=landscape",
  "article-compare-06":
    "https://readdy.ai/api/search-image?query=Comparison%20split%20screen%20concept%2C%20two%20smartphones%20side%20by%20side%2C%20minimalist%20tech%20comparison%2C%20warm%20neutral%20tones%2C%20clean%20professional%20photography%2C%20soft%20lighting&width=800&height=450&seq=article-compare-06&orientation=landscape",
  "article-privacy-07":
    "https://readdy.ai/api/search-image?query=Privacy%20protection%20concept%2C%20document%20with%20shield%20icon%2C%20warm%20professional%20atmosphere%2C%20minimalist%20legal%20theme%2C%20clean%20modern%20design%2C%20soft%20lighting&width=800&height=450&seq=article-privacy-07&orientation=landscape",
  "article-streamer-08":
    "https://readdy.ai/api/search-image?query=Professional%20streaming%20setup%20with%20microphone%20and%20lighting%2C%20minimalist%20content%20creator%20workspace%2C%20warm%20atmosphere%2C%20modern%20tech%20environment%2C%20clean%20editorial%20style&width=800&height=450&seq=article-streamer-08&orientation=landscape",
  "article-trend-09":
    "https://readdy.ai/api/search-image?query=Data%20analytics%20dashboard%20with%20charts%20and%20graphs%2C%20minimalist%20business%20intelligence%20concept%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design%2C%20soft%20lighting&width=800&height=450&seq=article-trend-09&orientation=landscape",
  "article-support-10":
    "https://readdy.ai/api/search-image?query=Customer%20support%20headset%20on%20desk%2C%20help%20desk%20concept%2C%20minimalist%20office%20scene%2C%20warm%20professional%20atmosphere%2C%20clean%20photography%2C%20soft%20lighting&width=800&height=450&seq=article-support-10&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground-950 mb-2">
            文章未找到
          </h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: "91直播平台资讯站",
    },
    publisher: {
      "@type": "Organization",
      name: "91直播平台资讯站",
    },
  };

  const imgSrc = articleImages[article.imageSeq] || articleImages["article-intro-01"];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`91直播平台, ${article.title}, ${category ? category.name : ""}`}
        ogType="article"
      />
      <SchemaOrg data={articleStructuredData} />

      <div className="pt-16">
        <Breadcrumb
          items={[
            ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
            { label: article.title },
          ]}
        />

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-500">
                    {article.date}
                  </span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
              </header>

              {/* Featured Image */}
              <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-10">
                <img
                  src={imgSrc}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-content text-sm md:text-base text-foreground-800 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Article Footer */}
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-4">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>
                      返回{category.name}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => {
                    const raImg = articleImages[ra.imageSeq] || articleImages["article-intro-01"];
                    return (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 flex flex-col"
                      >
                        <div className="relative w-full h-36 overflow-hidden">
                          <img
                            src={raImg}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-foreground-500">{ra.date}</span>
                          </div>
                          <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                            {ra.title}
                          </h3>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}